import {
  FunctionComponent,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DraftConfiguration, Point, PrintArea, Size } from '@/types';
import { addVectors } from '@/utils/math/addVectors';
import { arePerpendicular } from '@/utils/math/arePerpendicular';
import { degreeToRad } from '@/utils/math/degreeToRad';
import { getDistanceFromParallelLines } from '@/utils/math/getDistanceFromParallelLines';
import { getDistanceFromPointToLine } from '@/utils/math/getDistanceFromPointToLine';
import { getLine } from '@/utils/math/getLine';
import { getRotatedRect } from '@/utils/math/getRotatedRect';
import { getSvgLine } from '@/utils/math/getSvgLine';
import { rotatePoint } from '@/utils/math/rotatePoint';
import { subtractVectors } from '@/utils/math/subtractVectors';
import { Line, SvgLine } from '@/utils/math/types';
import { getHasSoftBoundary } from '@/model/printArea/getHasSoftBoundary';
import { getPrintAreaSize } from '@/model/printArea/getPrintAreaSize';

type OnSnap = (isSnapped: boolean) => void;

interface SnapLineItem {
  lines: Line[];
  onSnap: OnSnap;
}

type SetSnapLines = (
  id: number,
  offset: Point,
  rotation: number,
  size: Size,
  onSnap: OnSnap
) => void;

type RemoveSnapLines = (id: number) => void;

type MatchSnapLines = (
  configuration: DraftConfiguration,
  configurationSize: Size,
  offsetDelta: Point,
  globalToLocalFactor: number,
  snapLinesActive?: boolean
) => [Point, Line[], SnapLineItem[]];

const missingProvider = () => {
  throw new Error('Missing SnapLinesProvider');
};
const SnapLineContext = createContext({
  setSnapLines: missingProvider as SetSnapLines,
  removeSnapLines: missingProvider as RemoveSnapLines,
  matchSnapLines: missingProvider as MatchSnapLines,
});

const getEmptyMap = () => new Map();

export const useSnapLinesProvider = () => {
  const snapLineItems = useRef<Map<number, SnapLineItem>>(getEmptyMap());

  const setSnapLines = useCallback(
    (
      id: number,
      offset: Point,
      rotation: number,
      size: Size,
      onSnap: (isSnapped: boolean) => void
    ) => {
      snapLineItems.current.set(id, {
        lines: calculateSnapLines(offset, rotation, size),
        onSnap,
      });
    },
    []
  );

  const removeSnapLines = useCallback((id: number) => {
    snapLineItems.current.delete(id);
  }, []);

  const matchSnapLines = useCallback<MatchSnapLines>(
    (configuration, configurationSize, offsetDelta, globalToLocalFactor, snapLinesActive?) => {
      const { offset, rotation } = configuration;
      const snapThreshold = 10 * globalToLocalFactor;

      const newOffset = addVectors(offset, offsetDelta);

      const ownSnapLines = calculateSnapLines(newOffset, rotation, configurationSize);
      if (!ownSnapLines || snapLinesActive === false) {
        return [newOffset, [], []];
      }

      const ownRect = getRotatedSnapPoints(newOffset, rotation, configurationSize);
      const ownSnapPoints = [
        ownRect.topLeft,
        ownRect.topRight,
        ownRect.bottomLeft,
        ownRect.bottomRight,
        ownRect.center,
      ];

      const otherSnapLineItems: SnapLineItem[] = [];
      const otherSnapLines: Line[] = [];
      snapLineItems.current.forEach((snapLineItem, key) => {
        if (key !== configuration.cid) {
          otherSnapLineItems.push(snapLineItem);
          otherSnapLines.push(...snapLineItem.lines);
        }
      });

      // find all snap lines with snap distance less than or equal the snap threshold
      const matches: { distance: number; snapDifference: Point; snapLine: Line }[] = [];
      for (const ownSnapLine of ownSnapLines) {
        for (const snapLine of otherSnapLines) {
          const [distance, snapDifference] = getDistanceFromParallelLines(snapLine, ownSnapLine);
          if (distance <= snapThreshold) {
            matches.push({ distance, snapDifference, snapLine });
          }
        }
      }

      // find all snap points with snap distance less than or equal the snap threshold
      for (const ownSnapPoint of ownSnapPoints) {
        for (const snapLine of otherSnapLines) {
          const [distance, snapDifference] = getDistanceFromPointToLine(snapLine, ownSnapPoint);
          if (distance <= snapThreshold) {
            matches.push({ distance, snapDifference, snapLine });
          }
        }
      }

      if (matches.length === 0) {
        otherSnapLineItems.forEach((item) => item.onSnap(false));
        return [newOffset, [], []];
      }

      matches.sort(({ distance: distanceA }, { distance: distanceB }) => distanceA - distanceB);

      const snappedLines: Line[] = [];
      const { snapLine: closestSnapLine, snapDifference } = matches.shift()!;
      snappedLines.push(closestSnapLine);
      newOffset.x += snapDifference.x;
      newOffset.y += snapDifference.y;

      // find the closest perpendicular snap line to the closest snap line
      const perpendicularMatch = matches.find(({ snapLine }) =>
        arePerpendicular(closestSnapLine, snapLine)
      );
      if (perpendicularMatch) {
        const { snapLine: perpendicularSnapLine, snapDifference } = perpendicularMatch;
        snappedLines.push(perpendicularSnapLine);
        newOffset.x += snapDifference.x;
        newOffset.y += snapDifference.y;
      }

      const snappedLineItems = otherSnapLineItems.filter((item) => {
        const isSnapTarget = item.lines.some((line) => snappedLines.includes(line));
        item.onSnap(isSnapTarget);
        return isSnapTarget;
      });

      return [newOffset, snappedLines, snappedLineItems];
    },
    []
  );

  const providerValue = useMemo(
    () => ({ matchSnapLines, setSnapLines, removeSnapLines }),
    [matchSnapLines, setSnapLines, removeSnapLines]
  );

  return useCallback<FunctionComponent<{ children: ReactNode }>>(
    ({ children }) => (
      <SnapLineContext.Provider value={providerValue}>{children}</SnapLineContext.Provider>
    ),
    [providerValue]
  );
};

const SNAP_ID_PRINT_AREA = -1;
export const useSnapLinesPrintArea = (printArea: PrintArea) => {
  const { setSnapLines, removeSnapLines } = useContext(SnapLineContext);
  const [isSnapTarget, setIsSnapTarget] = useState(false);
  const onSnap = useCallback<OnSnap>((isSnapped) => setIsSnapTarget(isSnapped), []);
  useEffect(() => {
    setSnapLines(SNAP_ID_PRINT_AREA, { x: 0, y: 0 }, 0, getPrintAreaSize(printArea), onSnap);
    return () => removeSnapLines(SNAP_ID_PRINT_AREA);
  }, [setSnapLines, removeSnapLines, printArea, onSnap]);

  return isSnapTarget;
};

export const useSnapLines = (
  printArea: PrintArea,
  configuration: DraftConfiguration,
  configurationSize: Size
): [
  boolean,
  SvgLine[],
  (delta: Point, rotation: number, factor: number, snapLinesActive: boolean) => Point,
  () => void
] => {
  const { matchSnapLines, setSnapLines, removeSnapLines } = useContext(SnapLineContext);
  const [isSnapTarget, setIsSnapTarget] = useState(false);
  const onSnap = useCallback<OnSnap>((isSnapped) => setIsSnapTarget(isSnapped), []);
  const [snapSvgLines, setSnapSvgLines] = useState<SvgLine[]>([]);
  const snappedItems = useRef<SnapLineItem[]>([]);

  const { cid, offset, rotation } = configuration;
  const { width, height, unit } = configurationSize;
  // configurationSize is always a new size object so splitting and recreating it
  //  saves us wasted effect executions while still being safer than ignoring it in the deps array
  useEffect(() => {
    setSnapLines(cid, offset, rotation, { width, height, unit }, onSnap);
    return () => removeSnapLines(cid);
  }, [setSnapLines, removeSnapLines, cid, offset, rotation, width, height, unit, onSnap]);

  const onMove = (delta: Point, rotation: number, factor: number, snapLinesActive: boolean) => {
    let offsetDelta = delta;

    if (!getHasSoftBoundary(printArea.boundary)) {
      offsetDelta = clampDeltaToPrintArea(printArea, delta, offset, rotation, configurationSize);
    }

    const [newOffset, snappedLines, snappedStoreItems] = matchSnapLines(
      configuration,
      configurationSize,
      offsetDelta,
      factor,
      snapLinesActive
    );

    setSnapSvgLines(snappedLines.map((l) => getSvgLine(l, 4000)));
    snappedItems.current = snappedStoreItems;

    return newOffset;
  };

  const resetSnapLines = () => {
    if (setSnapSvgLines.length) {
      setSnapSvgLines([]);
      snappedItems.current.forEach((snappedItem) => snappedItem.onSnap(false));
    }
  };

  return [isSnapTarget, snapSvgLines, onMove, resetSnapLines];
};

const getRotatedSnapPoints = (offset: Point, rotationInDeg: number, size: Size) => {
  const rotation = degreeToRad(rotationInDeg);
  const center = {
    x: offset.x + size.width / 2,
    y: offset.y + size.height / 2,
  };

  return {
    topLeft: rotatePoint(offset.x, offset.y, rotation, center.x, center.y),
    topRight: rotatePoint(offset.x + size.width, offset.y, rotation, center.x, center.y),
    bottomLeft: rotatePoint(offset.x, offset.y + size.height, rotation, center.x, center.y),
    bottomRight: rotatePoint(
      offset.x + size.width,
      offset.y + size.height,
      rotation,
      center.x,
      center.y
    ),
    centerTop: rotatePoint(center.x, offset.y, rotation, center.x, center.y),
    centerBottom: rotatePoint(center.x, offset.y + size.height, rotation, center.x, center.y),
    centerLeft: rotatePoint(offset.x, center.y, rotation, center.x, center.y),
    centerRight: rotatePoint(offset.x + size.width, center.y, rotation, center.x, center.y),
    center: rotatePoint(center.x, center.y, rotation, center.x, center.y),
  };
};

const calculateSnapLines = (offset: Point, rotation: number, size: Size): Line[] => {
  if (!size) {
    return [];
  }

  const rect = getRotatedSnapPoints(offset, rotation, size);

  return [
    getLine(rect.topLeft, rect.bottomLeft),
    getLine(rect.topLeft, rect.topRight),
    getLine(rect.topRight, rect.bottomRight),
    getLine(rect.bottomLeft, rect.bottomRight),
    getLine(rect.centerTop, rect.centerBottom),
    getLine(rect.centerLeft, rect.centerRight),
  ];
};

const clampDeltaToPrintArea = (
  {
    boundary: {
      size: { width: paWidth, height: paHeight },
    },
  }: PrintArea,
  delta: Point,
  initialOffset: Point,
  rotation: number,
  configurationSize: Size
): Point => {
  const potentialOffset = addVectors(initialOffset, delta);

  const rect = {
    x: potentialOffset.x,
    y: potentialOffset.y,
    width: configurationSize.width,
    height: configurationSize.height,
  };

  const { x, y, width, height } = getRotatedRect(rotation, rect);

  if (paWidth > width) {
    if (x + width > paWidth) {
      potentialOffset.x -= x + width - paWidth;
    }
    if (x < 0) {
      potentialOffset.x -= x;
    }
  }

  if (paHeight > height) {
    if (y + height > paHeight) {
      potentialOffset.y -= y + height - paHeight;
    }
    if (y < 0) {
      potentialOffset.y -= y;
    }
  }

  return subtractVectors(potentialOffset, initialOffset);
};
